import React from 'react';
import { useRouteError } from 'react-router-dom';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: '600px', margin: '80px auto', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>!</div>
          <h1 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '8px', color: 'hsl(var(--text-primary))' }}>Something went wrong</h1>
          <p style={{ color: 'hsl(var(--text-muted))', marginBottom: '24px', fontSize: '16px' }}>
            The application encountered an unexpected error. Please try refreshing the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px', backgroundColor: 'hsl(var(--brand-navy))', color: '#fff',
              border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Refresh Page
          </button>
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <details style={{ marginTop: '24px', textAlign: 'left', background: 'hsl(var(--bg-sunken))', color: 'hsl(var(--text-primary))', padding: '16px', borderRadius: '8px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: '500' }}>Error Details</summary>
              <pre style={{ fontSize: '12px', overflow: 'auto', marginTop: '8px', whiteSpace: 'pre-wrap' }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

/** Route-level error element for the data router. The data router catches render
 *  errors at the route boundary (not the top-level ErrorBoundary), so without this
 *  a crash shows React Router's bare default page. Renders the same friendly UI. */
export function RouteError() {
  const error = useRouteError();
  if (process.env.NODE_ENV !== 'production') console.error('RouteError caught:', error);
  return (
    <div style={{ padding: '40px', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: '600px', margin: '80px auto', textAlign: 'center' }} data-testid="route-error">
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>!</div>
      <h1 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '8px', color: 'hsl(var(--text-primary))' }}>Something went wrong</h1>
      <p style={{ color: 'hsl(var(--text-muted))', marginBottom: '24px', fontSize: '16px' }}>
        This page hit an unexpected error. Please try refreshing.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '10px 24px', backgroundColor: 'hsl(var(--brand-navy))', color: '#fff',
          border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500',
          cursor: 'pointer'
        }}
      >
        Refresh Page
      </button>
      {process.env.NODE_ENV !== 'production' && error && (
        <details style={{ marginTop: '24px', textAlign: 'left', background: 'hsl(var(--bg-sunken))', color: 'hsl(var(--text-primary))', padding: '16px', borderRadius: '8px' }}>
          <summary style={{ cursor: 'pointer', fontWeight: '500' }}>Error Details</summary>
          <pre style={{ fontSize: '12px', overflow: 'auto', marginTop: '8px', whiteSpace: 'pre-wrap' }}>
            {(error?.stack || error?.message || String(error))}
          </pre>
        </details>
      )}
    </div>
  );
}
